export interface Discount {
	id?: number;
	type: 'fixed' | 'percentage';
	value: number;
	allServices: boolean;
	targetIds: number[];
}

export interface Service {
	id: number;
	name: string;
	description: string;
	categoryId: number;
	priceCents: number;
	parentId: number | null;
	includedIds: number[];
	discounts: Discount[];
}

export interface Category {
	id: number;
	name: string;
}

export interface Catalog {
	services: Service[];
	categories: Category[];
}

export const startingPrice = (cents: number) =>
	`החל מ־${new Intl.NumberFormat('he-IL', {
		style: 'currency',
		currency: 'ILS',
		maximumFractionDigits: 2,
	}).format(cents / 100)}`;

export function calculatePricing(services: Service[], explicitIds: number[]) {
	const explicit = new Set(explicitIds);
	const includedBy = new Map<number, Set<number>>();
	const byId = new Map(services.map((s) => [s.id, s]));

	for (const root of explicit) {
		const visited = new Set<number>([root]);

		const walk = (id: number) => {
			for (const target of byId.get(id)?.includedIds || []) {
				if (visited.has(target) || !byId.has(target)) continue;

				visited.add(target);

				if (!includedBy.has(target)) includedBy.set(target, new Set());

				includedBy.get(target)!.add(root);
				walk(target);
			}
		};

		walk(root);
	}

	// Only explicit user selections activate discounts, including explicit selections that are also included.
	const rules = services
		.filter((s) => explicit.has(s.id))
		.flatMap((owner) =>
			owner.discounts.map((rule) => ({ ...rule, ownerName: owner.name }))
		);
	const rows = services
		.filter((s) => explicit.has(s.id) || includedBy.has(s.id))
		.map((service) => {
			const includers = Array.from(includedBy.get(service.id) || []).map(
				(id) => byId.get(id)!.name
			);
			const specific = rules.filter(
				(r) => !r.allServices && r.targetIds.includes(service.id)
			);
			const applicable = specific.length
				? specific
				: rules.filter((r) => r.allServices);

			const reduction = (r: Discount) =>
				Math.min(
					service.priceCents,
					r.type === 'fixed'
						? r.value
						: Math.round((service.priceCents * r.value) / 10000)
				);

			const discount = includers.length
				? undefined
				: applicable.reduce<(typeof applicable)[number] | undefined>(
						(best, r) => (!best || reduction(r) > reduction(best) ? r : best),
						undefined
				  );
			const savings = discount ? reduction(discount) : 0;

			return {
				service,
				explicit: explicit.has(service.id),
				includers,
				discount,
				savings,
				priceCents: includers.length
					? 0
					: Math.max(0, service.priceCents - savings),
			};
		});

	return { rows, totalCents: rows.reduce((sum, r) => sum + r.priceCents, 0) };
}

export type Estimate = ReturnType<typeof calculatePricing>;

export function discountText(rule: Discount) {
	return rule.type === 'percentage'
		? `${rule.value / 100}%`
		: `${new Intl.NumberFormat('he-IL', {
				style: 'currency',
				currency: 'ILS',
		  }).format(rule.value / 100)}`;
}

export function inquiryBreakdown(estimate: Estimate) {
	return [
		'בקשת מידע על שירותים - אינה הזמנה',
		...estimate.rows.map(
			(r) =>
				`${r.service.name} | ${
					r.explicit ? 'נבחר במפורש' : 'נכלל אוטומטית'
				} | מחיר התחלתי: ${startingPrice(r.service.priceCents)} | ${
					r.includers.length
						? `כלול במחיר: ${r.includers.join(', ')}`
						: r.discount
						? `הנחה: ${discountText(r.discount)} בעקבות בחירת ${
								r.discount.ownerName
						  }; לאחר הנחה: ${startingPrice(r.priceCents)}`
						: startingPrice(r.priceCents)
				}`
		),
		`סך הערכת המחיר: ${startingPrice(estimate.totalCents)}`,
		'המחיר הסופי תלוי בפרטי האירוע ובמספר האורחים, ככל שרלוונטי.',
	].join('\n');
}
